import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Header from "./components/Header";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import SchedulingInbox from "./components/SchedulingInbox";
import GoalInput from "./components/GoalInput";
import FloatingBanner from "./components/FloatingBanner";
import TranscribeSession from "./components/TranscribeSession";

function TherapistDashboard({ user }) {
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [coachId, setCoachId] = useState("");
  const [mySessions, setMySessions] = useState([]);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [sessionTags, setSessionTags] = useState([]);
  const [privateNotes, setPrivateNotes] = useState("");
  const [enneagram, setEnneagram] = useState("");
  const [goals, setGoals] = useState([]);
  const [strongSelfActivation, setStrongSelfActivation] = useState("");
  const [personalityTypeIdentification, setPersonalityTypeIdentification] = useState("");
  const [copingStrategyIdentification, setCopingStrategyIdentification] = useState("");
  const [reprogrammingForSuccess, setReprogrammingForSuccess] = useState("");
  const [emotionalEmpowerment, setEmotionalEmpowerment] = useState("");
  const [nextSteps, setNextSteps] = useState("");
  const [files, setFiles] = useState([]);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const { t } = useTranslation();
  const [sessionDateTime, setSessionDateTime] = useState(new Date());
  const [athleteSessions, setAthleteSessions] = useState([]);
  const [creatingNewSession, setCreatingNewSession] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [activeTab, setActiveTab] = useState("sessions");
  const [moodEntries, setMoodEntries] = useState([]);
  const [hasSchedulingNotification, setHasSchedulingNotification] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(60); // default to 60 minutes
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const availableTags = [
    "Focus", "Motivation", "Stress", "Recovery", "Confidence", "Mindset", "Performance"
  ];

  const fetchMoodEntries = async (athleteId) => {
    try {
      const ref = collection(db, "users", athleteId, "moodCheckins");
      const snapshot = await getDocs(ref);
      const moods = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return b.createdAt.seconds - a.createdAt.seconds;
        });
      setMoodEntries(moods);
    } catch (e) {
      console.error("Error fetching moods:", e);
    }
  };

  const [mbti, setMbti] = useState({
    energy: "",
    info: "",
    decision: "",
    lifestyle: "",
  });

  const saveMBTI = async () => {
    if (!selectedAthlete) return setErrorMessage(t("select_athlete_first"));

    const mbtiType = mbti.energy + mbti.info + mbti.decision + mbti.lifestyle;

    try {
      const userRef = doc(db, "users", selectedAthlete);
      await updateDoc(userRef, { mbti: mbtiType });
      setSuccessMessage(t("mbti_saved") + mbtiType);

    } catch (error) {
      setErrorMessage(t("error_saving_mbti"));
    }
  };

  const exportToPDF = () => {
    const input = document.getElementById("session-list");

    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("sessions.pdf");
    });
  };

  const selectedAthleteObj = athletes.find((a) => a.id === selectedAthlete);
  let athleteAge = null;

  if (selectedAthleteObj?.birthDate) {
    const birth = new Date(selectedAthleteObj.birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    athleteAge = age;
  }

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserId(user.uid);
    }
  }, []);

  useEffect(() => {
    if (!selectedAthlete) {
      setCreatingNewSession(true);
      setAthleteSessions([]);
      return;
    }

    const athlete = athletes.find((a) => a.id === selectedAthlete);
    setCoachId(athlete?.coachId || "");

    const filtered = mySessions.filter(
      (s) =>
        s.athleteId === selectedAthlete &&
        new Date(s.timestamp?.toDate?.() || s.timestamp) < new Date()
    );
    setAthleteSessions(filtered);

    const isNewAthlete = filtered.length === 0;
    setCreatingNewSession(isNewAthlete);

    if (isNewAthlete) {
      setEditingSessionId(null);
      setSessionNotes("");
      setPrivateNotes("");
      setGoals([]);
      setStrongSelfActivation("");
      setPersonalityTypeIdentification("");
      setCopingStrategyIdentification("");
      setReprogrammingForSuccess("");
      setEmotionalEmpowerment("");
      setNextSteps("");
      setSessionDateTime(new Date());
      setSessionTags([]);
      setFiles([]);
    }

  }, [selectedAthlete, athletes, mySessions]);

  useEffect(() => {
    const fetchAthletes = async () => {
      const snapshot = await getDocs(collection(db, "users"));

      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAllUsers(users); // ✅ new: set all users for coach email lookup
      setAthletes(users.filter((user) => user.role === "athlete")); // ✅ still used for dropdown
    };

    fetchAthletes();
  }, []);

  useEffect(() => {
    const fetchAthleteDetails = async () => {
      if (!selectedAthlete) return;

      const athlete = athletes.find((a) => a.id === selectedAthlete);
      setCoachId(athlete?.coachId || "");

      try {
        const userDocSnap = await getDoc(doc(db, "users", selectedAthlete));
        const userData = userDocSnap.exists() ? userDocSnap.data() : {};

        // Load MBTI & Enneagram
        const mbtiStr = userData.mbti || "";
        setMbti({
          energy: mbtiStr[0] || "",
          info: mbtiStr[1] || "",
          decision: mbtiStr[2] || "",
          lifestyle: mbtiStr[3] || "",
        });
        setEnneagram(userData.enneagram || "");

        // Load persistent goals
        const persistentGoals = userData.goals || [];

        // ✅ Fetch latest session for this athlete
        const q = query(
          collection(db, "sessions"),
          where("athleteId", "==", selectedAthlete)
        );
        const sessionSnapshot = await getDocs(q);
        const sessions = sessionSnapshot.docs
          .map((doc) => doc.data())
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const latestSessionGoals = sessions[0]?.goals || [];

        // Create a lookup map from goal text to latest score/comment
        const latestMap = {};
        latestSessionGoals.forEach(goal => {
          const last = goal.progress?.[goal.progress.length - 1];
          if (last) {
            latestMap[goal.text] = {
              currentScore: last.score || "",
              currentComment: last.comment || "",
            };
          }
        });

        const enrichedGoals = persistentGoals.map((goal) => ({
          ...goal,
          currentScore: latestMap[goal.text]?.currentScore || "",
          currentComment: latestMap[goal.text]?.currentComment || "",
        }));

        setGoals(enrichedGoals);
      } catch (error) {
        console.error(t("error_fetching_mbti"), error.message);
      }
    };

    fetchAthleteDetails();
  }, [selectedAthlete]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.warn(t("user_not_logged_in"));
        return;
      }

      try {
        const therapistId = user.uid;

        const q = query(
          collection(db, "sessions"),
          where("therapistId", "==", therapistId)
        );

        const querySnapshot = await getDocs(q);
        const sessions = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setMySessions(sessions);
      } catch (error) {
        console.error(t("failed_to_fetch_sessions"), error.message);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshSessions = async (therapistId) => {
    const q = query(
      collection(db, "sessions"),
      where("therapistId", "==", therapistId)
    );

    const querySnapshot = await getDocs(q);
    const sessions = querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    setMySessions(sessions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAthlete || !sessionNotes) {
      setErrorMessage(t("enter_athlete_and_notes"));
      return;
    }

    const therapistId = auth.currentUser?.uid;
    if (!therapistId) return;

    const uploadedFileUrls = [];

    for (const file of files) {
      const storageRef = ref(storage, `session-files/${Date.now()}-${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      uploadedFileUrls.push(downloadURL);
    }

    try {
      // ✅ Define time info early
      const durationInMinutes = sessionDuration;
      const rounded15MinUnits = Math.ceil(durationInMinutes / 15);
      const hoursToDeduct = rounded15MinUnits * 0.25;

      // ✅ Define sessionData after time is available
      const sessionData = {
        therapistId,
        athleteId: selectedAthlete,
        coachId,
        notes: sessionNotes,
        privateNotes,
        tags: sessionTags,
        timestamp: sessionDateTime.toISOString(),
        goals: goals.map((goal) => {
          const progressEntry = {
            score: parseInt(goal.currentScore) || 0,
            comment: goal.currentComment || "",
            date: new Date().toISOString(),
          };

          return {
            text: goal.text,
            progress: [...(goal.progress || []), progressEntry],
          };
        }),
        strongSelfActivation,
        personalityTypeIdentification,
        copingStrategyIdentification,
        reprogrammingForSuccess,
        emotionalEmpowerment,
        nextSteps,
        files: uploadedFileUrls,
        durationInMinutes,
        hoursUsed: hoursToDeduct,
      };

      if (editingSessionId) {
        // 🔁 Update existing session
        await updateDoc(doc(db, "sessions", editingSessionId), sessionData);
        await refreshSessions(therapistId);
        setSuccessMessage(t("session_updated"));
      } else {
        // 🆕 Add new session
        await addDoc(collection(db, "sessions"), sessionData);
        await refreshSessions(therapistId);

        // 🧠 Save persistent athlete goals (excluding currentScore/comment)
        await updateDoc(doc(db, "users", selectedAthlete), {
          goals: goals.map((goal) => ({
            text: goal.text,
            progress: goal.progress || [],
          })),
        });

        // ⏱ Update user's used hours
        const athleteRef = doc(db, "users", selectedAthlete);
        const athleteSnap = await getDoc(athleteRef);

        if (athleteSnap.exists()) {
          const data = athleteSnap.data();
          const currentUsed = data.usedHours || 0;
          await updateDoc(athleteRef, {
            usedHours: currentUsed + hoursToDeduct,
          });
        }

        setSuccessMessage(t("session_saved"));
      }

      // ✅ Reset form
      setTimeout(() => setSuccessMessage(""), 3000);
      setSessionNotes("");
      setSelectedAthlete("");
      setSessionTags([]);
      setEditingSessionId(null);
      setPrivateNotes("");
      setFiles([]);
      setStrongSelfActivation("");
      setPersonalityTypeIdentification("");
      setCopingStrategyIdentification("");
      setReprogrammingForSuccess("");
      setEmotionalEmpowerment("");
      setNextSteps("");
      setCreatingNewSession(false);
    } catch (error) {
      setErrorMessage(t("error_saving_session") + error.message);
    }
  };

  const fetchAthleteJournals = async (athleteId) => {
    try {
      const journalRef = collection(db, "users", athleteId, "journalEntries");
      const snapshot = await getDocs(journalRef);
      const journals = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((entry) => entry.shareWithTherapist)
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
      setJournalEntries(journals);
    } catch (e) {
      console.error("Error fetching journal entries:", e);
    }
  };

  const handleDelete = async (sessionId) => {
    const confirmed = window.confirm(t("confirm_delete_session"));
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "sessions", sessionId));
      setMySessions((prev) => prev.filter((s) => s.id !== sessionId));
      setSuccessMessage(t("session_deleted"));
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(t("failed_to_delete_session") + error.message);
    }
  };
  const getUserName = (uid) => {
    const user = athletes.find((a) => a.id === uid);
    return user ? user.name || user.email : uid;
  };
  const getEmailById = (uid) => {
    const user = allUsers.find((u) => u.id === uid);
    return user ? user.name || user.email : uid;
  };
  const now = new Date();
  const filteredSessions = mySessions.filter(
    (session) => session.athleteId === selectedAthlete
  );

  const formatDateTimeLocal = (date) => {
    const pad = (n) => n.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <><Header user={user} />
      <>
        {successMessage && (
          <FloatingBanner
            type="success"
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <FloatingBanner
            type="error"
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}
      </>
      <div className="min-h-screen bg-white px-4 py-8">
        <div className="w-full max-w-5xl mx-auto bg-[#f1efe7] rounded-xl shadow-md p-4 sm:p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t("therapist_dashboard")}</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_athlete")}</label>
            <select
              value={selectedAthlete}
              onChange={(e) => {
                setSelectedAthlete(e.target.value);
                setCreatingNewSession(false);
                setExpandedSessionId(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
            >
              <option value="">{t("select_athlete")}</option>
              {athletes.map((athlete) => (
                <option key={athlete.id} value={athlete.id}>
                  {athlete.name || athlete.email}
                </option>
              ))}
            </select>
          </div>

          {athleteAge !== null && (
            <div className="text-sm text-gray-700 mt-1">
              🎂 {t("age")}: {athleteAge} {t("years_old") || "سنة"}
            </div>
          )}

          {selectedAthlete && athleteSessions.length > 0 && !creatingNewSession && (
            <div className="mb-6">
              <p className="text-gray-700 mb-4">

              </p>
              <button
                type="button"
                onClick={() => setCreatingNewSession(true)}
                className="bg-[#b22222] text-white px-4 py-2 rounded hover:bg-red-800 transition"
              >
                ➕ {t("start_new_session")}
              </button>
            </div>
          )}

          {selectedAthlete && athleteSessions.length === 0 && !creatingNewSession && (
            <p className="text-gray-600 mb-6">{t("no_sessions_yet")}</p>
          )}

          {selectedAthlete && creatingNewSession && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-8">
              {selectedAthlete && (
                <>
                  <div className="my-6 p-4 bg-white rounded shadow">
                    <h3 className="text-lg font-semibold mb-2">{t("mbti_title")}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium">{t("mbti_energy")}</label>
                        <select value={mbti.energy} onChange={(e) => setMbti((prev) => ({ ...prev, energy: e.target.value }))}>
                          <option value="">--</option>
                          <option value="E">{t("mbti_extraversion")}</option>
                          <option value="I">{t("mbti_introversion")}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-medium">{t("mbti_info")}</label>
                        <select value={mbti.info} onChange={(e) => setMbti((prev) => ({ ...prev, info: e.target.value }))}>
                          <option value="">--</option>
                          <option value="S">{t("mbti_sensing")}</option>
                          <option value="N">{t("mbti_intuition")}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-medium">{t("mbti_decision")}</label>
                        <select value={mbti.decision} onChange={(e) => setMbti((prev) => ({ ...prev, decision: e.target.value }))}>
                          <option value="">--</option>
                          <option value="T">{t("mbti_thinking")}</option>
                          <option value="F">{t("mbti_feeling")}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-medium">{t("mbti_lifestyle")}</label>
                        <select value={mbti.lifestyle} onChange={(e) => setMbti((prev) => ({ ...prev, lifestyle: e.target.value }))}>
                          <option value="">--</option>
                          <option value="J">{t("mbti_judging")}</option>
                          <option value="P">{t("mbti_perceiving")}</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="button"  // ✅ prevent this button from triggering form submit
                      onClick={(e) => {
                        e.preventDefault(); // ✅ prevent reload or form triggering
                        if (!selectedAthlete) return setErrorMessage(t("alert_select_athlete"));
                        saveMBTI();
                      }}
                      className="mt-4 bg-[#b22222] text-white px-4 py-2 rounded hover:bg-red-800 transition"
                    >
                      Save MBTI
                    </button>
                  </div>

                  <div className="my-6 p-4 bg-white rounded shadow">
                    <h3 className="text-lg font-semibold mb-2">{t("enneagram_title")}</h3>
                    <select
                      value={enneagram}
                      onChange={(e) => setEnneagram(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                    >
                      <option value="">{t("select_placeholder")}</option>
                      {[...Array(9)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault(); // ✅ Important to stop form submit
                        if (!selectedAthlete) return setErrorMessage(t("alert_select_athlete"));
                        try {
                          await updateDoc(doc(db, "users", selectedAthlete), { enneagram });
                          setSuccessMessage(t("enneagram_saved"));
                        } catch (error) {
                          setErrorMessage(t("enneagram_failed") + error.message);
                        }
                      }}
                      className="mt-4 bg-[#b22222] text-white px-4 py-2 rounded hover:bg-red-800 transition"
                    >
                      {t("save_enneagram")}
                    </button>
                  </div>

                  <div className="space-y-4 bg-white rounded p-4 shadow mt-6">
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{t("psychological_development")}</h3>
                    <GoalInput goals={goals} setGoals={setGoals} />

                    <div>
                      <label className="block font-medium text-sm text-gray-700">{t("strong_self_activation")}</label>
                      <textarea value={strongSelfActivation} onChange={(e) => setStrongSelfActivation(e.target.value)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block font-medium text-sm text-gray-700">{t("personality_type_identification")}</label>
                      <textarea value={personalityTypeIdentification} onChange={(e) => setPersonalityTypeIdentification(e.target.value)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block font-medium text-sm text-gray-700">{t("coping_strategy_identification")}</label>
                      <textarea value={copingStrategyIdentification} onChange={(e) => setCopingStrategyIdentification(e.target.value)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block font-medium text-sm text-gray-700">{t("reprogramming_for_success")}</label>
                      <textarea value={reprogrammingForSuccess} onChange={(e) => setReprogrammingForSuccess(e.target.value)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block font-medium text-sm text-gray-700">{t("emotional_empowerment")}</label>
                      <textarea value={emotionalEmpowerment} onChange={(e) => setEmotionalEmpowerment(e.target.value)} className="w-full border rounded px-3 py-2" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("session_date_time")}</label>
                    <input
                      type="datetime-local"
                      value={formatDateTimeLocal(sessionDateTime)}
                      onChange={(e) => setSessionDateTime(new Date(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("session_duration")}
                    </label>
                    <input
                      type="number"
                      min="15"
                      step="15"
                      value={sessionDuration}
                      onChange={(e) => setSessionDuration(parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                      placeholder="Enter duration in minutes (e.g., 60)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("session_notes")}</label>
                    <textarea
                      rows="5"
                      value={sessionNotes}
                      onChange={(e) => setSessionNotes(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                      placeholder={t("session_notes_placeholder")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("next_steps")}</label>
                    <textarea
                      rows="3"
                      value={nextSteps}
                      onChange={(e) => setNextSteps(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                      placeholder={t("next_steps_placeholder")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("tags")}</label>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.map((tag) => (
                        <button
                          type="button"
                          key={tag}
                          className={`px-3 py-1 rounded-full text-sm border ${sessionTags.includes(tag)
                            ? "bg-[#b22222] text-white border-[#b22222]"
                            : "bg-white text-gray-700 border-gray-300"
                            }`}
                          onClick={() => {
                            if (sessionTags.includes(tag)) {
                              setSessionTags(sessionTags.filter((t) => t !== tag));
                            } else {
                              setSessionTags([...sessionTags, tag]);
                            }
                          }}
                        >
                          {t(`tag_${tag.toLowerCase()}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("private_notes")}</label>
                    <textarea
                      rows="4"
                      value={privateNotes}
                      onChange={(e) => setPrivateNotes(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                      placeholder={t("private_notes_placeholder")}
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      type="submit"
                      className="bg-[#b22222] text-white px-4 py-2 rounded-md hover:bg-red-800 transition"
                    >
                      {editingSessionId ? t("update_session") : t("save_session")}
                    </button>
                    {editingSessionId && (
                      <button
                        type="button"
                        onClick={() => {
                          setSessionNotes("");
                          setSelectedAthlete("");
                          setEditingSessionId(null);
                          setCreatingNewSession(false);
                        }}
                        className="text-sm text-gray-500 underline"
                      >
                        ❌ {t("cancel_edit")}
                      </button>
                    )}
                  </div>
                </>
              )}
            </form>
          )}

          {selectedAthlete && (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex overflow-x-auto whitespace-nowrap -mb-px border-b border-gray-300 mb-6 scrollbar-hide">
                  {[
                    { key: "sessions", label: t("sessions_tab") },
                    { key: "journal", label: t("journal_tab") },
                    { key: "mood", label: t("mood_tab") },
                    { key: "notifications", label: t("notifications_tab") },
                    { key: "hours", label: t("hours_tab") },
                  ].map((tab, index) => (
                    <div
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key);
                        if (tab.key === "journal" && selectedAthlete) {
                          fetchAthleteJournals(selectedAthlete);
                        }
                        if (tab.key === "mood" && selectedAthlete) {
                          fetchMoodEntries(selectedAthlete);
                        }
                      }}
                      className={`cursor-pointer px-5 py-2 text-sm font-medium border border-b-0 rounded-t-md transition ${activeTab === tab.key
                        ? "bg-[#b22222] text-white border-[#b22222]"
                        : "bg-white text-gray-700 border-gray-300 hover:text-[#b22222]"
                        } ${index !== 0 ? "-ml-px" : ""}`}
                    >
                      {tab.label}
                    </div>
                  ))}
                </nav>
              </div>

              {activeTab === "journal" && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold mb-2">🗂️ {t("athlete_journals")}</h3>
                  {journalEntries.length === 0 ? (
                    <p className="text-gray-500">{t("no_journal_entries")}</p>
                  ) : (
                    journalEntries.map((entry) => (
                      <div key={entry.id} className="bg-white border rounded-md p-4 mb-3 shadow-sm">
                        <p className="text-xs text-gray-500">{new Date(entry.createdAt.seconds * 1000).toLocaleString()}</p>
                        <h4 className="text-md font-semibold">{entry.title}</h4>
                        <p className="text-gray-700 whitespace-pre-line">{entry.content}</p>
                        {entry.tags?.length > 0 && (
                          <p className="text-xs mt-1 text-gray-500">{t("tags")}: {entry.tags.join(", ")}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "sessions" && (
                <>
                  {selectedAthlete && !creatingNewSession && (
                    <>
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">🗂️ {t("past_sessions")}</h3>

                      <div id="session-list">
                        {filteredSessions.length === 0 ? (
                          <p className="text-gray-600">{t("no_sessions")}</p>
                        ) : (

                          <ul className="space-y-4">
                            {filteredSessions.map((session) => {
                              const isExpanded = expandedSessionId === session.id;

                              return (
                                <li
                                  key={session.id}
                                  className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm cursor-pointer"
                                  onClick={() =>
                                    setExpandedSessionId(isExpanded ? null : session.id)
                                  }
                                >
                                  {/* Collapsed summary */}
                                  <div className="flex justify-between items-center">
                                    <div>
                                      {new Date(session.timestamp).toLocaleDateString()}
                                    </div>
                                    {(session.athleteComment || session.coachComment) &&
                                      !session.commentSeenByTherapist && (
                                        <span className="text-xs bg-yellow-400 text-white px-2 py-0.5 rounded-full">
                                          🔔 {t("New_Comment")}
                                        </span>
                                      )}
                                  </div>

                                  {/* Expanded details */}
                                  {isExpanded && (
                                    <div className="mt-4 space-y-2">
                                      <div><strong>{t("coach")}:</strong> {getEmailById(session.coachId)}</div>
                                      <div><strong>{t("date")}:</strong> {new Date(session.timestamp).toLocaleString()}</div>
                                      <div><strong>{t("notes")}:</strong> {session.notes}</div>
                                      <div><strong>{t("private_notes")}:</strong> {session.privateNotes}</div>

                                      <div className="border-t pt-3 mt-3">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("psychological_development")}</h4>
                                        {session.goals && session.goals.length > 0 && (
                                          <div>
                                            <strong> {t("goal_identification")}:</strong>
                                            <ul className="list-disc list-inside">
                                              {session.goals.map((goal, idx) => {
                                                const latest = goal.progress?.[goal.progress.length - 1];
                                                return (
                                                  <li key={idx} className="mb-2">
                                                    {goal.text}
                                                    {latest && (
                                                      <>
                                                        — <strong>{latest.score}/10</strong> ({new Date(latest.date).toLocaleDateString()})
                                                        {latest.comment && (
                                                          <div className="text-xs text-gray-500 mt-1">
                                                            💬 {t("comment_justification")}: {latest.comment}
                                                          </div>
                                                        )}
                                                      </>
                                                    )}
                                                  </li>
                                                );
                                              })}

                                            </ul>
                                          </div>
                                        )}

                                        {session.strongSelfActivation && (
                                          <p><strong>{t("strong_self_activation")}:</strong> {session.strongSelfActivation}</p>
                                        )}
                                        {session.personalityTypeIdentification && (
                                          <p><strong>{t("personality_type_identification")}:</strong> {session.personalityTypeIdentification}</p>
                                        )}
                                        {session.copingStrategyIdentification && (
                                          <p><strong>{t("coping_strategy_identification")}:</strong> {session.copingStrategyIdentification}</p>
                                        )}
                                        {session.reprogrammingForSuccess && (
                                          <p><strong>{t("reprogramming_for_success")}:</strong> {session.reprogrammingForSuccess}</p>
                                        )}
                                        {session.emotionalEmpowerment && (
                                          <p><strong>{t("emotional_empowerment")}:</strong> {session.emotionalEmpowerment}</p>
                                        )}
                                      </div>
                                      {session.nextSteps && (
                                        <div className="mt-2 text-sm text-gray-800">
                                          <strong>{t("next_steps")}:</strong> {session.nextSteps}
                                        </div>
                                      )}

                                      {session.tags?.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                          {session.tags.map((tag, index) => (
                                            <span
                                              key={index}
                                              className="inline-block bg-[#b22222] text-white text-xs px-2 py-1 rounded-full"
                                            >
                                              {t(`tag_${tag.toLowerCase()}`) || tag}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      {session.athleteComment && (
                                        <div className="text-sm text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
                                          <strong>🧑‍🎓 {t("athlete_comment")}:</strong> {session.athleteComment}
                                        </div>
                                      )}

                                      {session.coachComment && (
                                        <div className="text-sm text-purple-700 bg-purple-50 p-2 rounded border border-purple-200">
                                          <strong>🎯 {t("coach_comment")}:</strong> {session.coachComment}
                                        </div>
                                      )}

                                      {session.files?.length > 0 && (
                                        <div className="mt-2">
                                          <strong>{t("attached_files")}:</strong>
                                          <ul className="list-disc list-inside">
                                            {session.files.map((url, index) => (
                                              <li key={index}>
                                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                                  {t("file")} {index + 1}
                                                </a>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      <div className="mt-2 flex gap-4">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedAthlete(session.athleteId);
                                            setCoachId(session.coachId);
                                            setSessionNotes(session.notes);
                                            setSessionTags(session.tags || []);
                                            setSessionDateTime(new Date(session.timestamp));
                                            setGoals(
                                              (session.goals || []).map((goal) => {
                                                const latest = goal.progress?.[goal.progress.length - 1] || {};
                                                return {
                                                  ...goal,
                                                  currentScore: latest.score || "",
                                                  currentComment: latest.comment || "",
                                                };
                                              })
                                            );
                                            setStrongSelfActivation(session.strongSelfActivation || "");
                                            setPersonalityTypeIdentification(session.personalityTypeIdentification || "");
                                            setCopingStrategyIdentification(session.copingStrategyIdentification || "");
                                            setReprogrammingForSuccess(session.reprogrammingForSuccess || "");
                                            setEmotionalEmpowerment(session.emotionalEmpowerment || "");
                                            setEditingSessionId(session.id);
                                            setCreatingNewSession(true);
                                            setPrivateNotes(session.privateNotes || "");
                                            setExpandedSessionId(null);
                                          }}
                                          className="text-[#b22222] hover:underline"
                                        >
                                          {t("edit")}
                                        </button>

                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(session.id);
                                          }}
                                          className="text-red-600 hover:underline"
                                        >
                                          {t("delete")}
                                        </button>
                                      </div>
                                    </div>

                                  )}
                                </li>
                              );
                            })}
                          </ul>

                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === "mood" && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold mb-2">{t("mood_logs")}</h3>
                  {moodEntries.length === 0 ? (
                    <p className="text-gray-500">{t("no_moods_yet")}</p>
                  ) : (
                    moodEntries.map(entry => (
                      <div key={entry.id} className="bg-white border rounded-md p-4 mb-3 shadow-sm">
                        <p className="text-xs text-gray-500">{new Date(entry.createdAt.seconds * 1000).toLocaleString()}</p>
                        <p className="text-md font-semibold">{entry.moodEmoji} {t(`mood_${entry.moodLabel.toLowerCase()}`)}</p>
                        {entry.note && (
                          <p className="text-sm mt-1 text-gray-700">{entry.note}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "notifications" && (
                <div className="space-y-6">
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <h3 className="text-lg font-semibold mb-4">{t("notifications")}</h3>
                    {!hasSchedulingNotification && (
                      <p className="text-sm text-gray-600">{t("no_notifications")}</p>
                    )}
                  </div>

                  {/* ✅ New scheduling request section */}
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <SchedulingInbox
                      therapistId={userId}
                      onStatusChange={(status) => {
                        setHasSchedulingNotification(
                          status === "pending" || status === "rejected"
                        );
                      }}
                    />
                  </div>
                </div>
              )}

              {activeTab === "hours" && (
                <div className="bg-white p-4 rounded shadow-sm rtl:text-right ltr:text-left" dir="auto">
                  <h3 className="text-lg font-semibold mb-4">{t("hours_summary")}</h3>
                  {(() => {
                    const athlete = athletes.find((a) => a.id === selectedAthlete);
                    const allocated = athlete?.allocatedHours || 0;
                    const used = athleteSessions.reduce((sum, session) => {
                      return sum + (session.hoursUsed ?? 0.25);
                    }, 0);
                    const remaining = (allocated - used).toFixed(2);

                    return (
                      <>
                        <div className="mb-4 text-sm text-gray-700 space-y-1">
                          <div>⏱ <strong>{t("total_allocated_hours")}:</strong> {allocated.toFixed(2)} {t("hours_unit") || "ساعة"}</div>
                          <div>🔄 <strong>{t("total_used_hours")}:</strong> {used.toFixed(2)} {t("hours_unit") || "ساعة"}</div>
                          <div>💡 <strong>{t("remaining_hours")}:</strong> {remaining} {t("hours_unit") || "ساعة"}</div>
                        </div>

                        <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden mb-4">
                          <div
                            className="bg-[#b22222] h-full transition-all duration-300"
                            style={{ width: `${Math.min((used / allocated) * 100, 100)}%` }}
                          />
                        </div>

                        <h4 className="font-semibold text-gray-800 mb-2">{t("session_usage_list")}</h4>
                        {athleteSessions.length === 0 ? (
                          <p className="text-gray-500">{t("no_sessions")}</p>
                        ) : (
                          <ul className="space-y-2">
                            {athleteSessions.map((session) => (
                              <li
                                key={session.id}
                                className="border border-gray-300 p-3 rounded-md text-sm bg-gray-50"
                              >
                                🗓 {new Date(session.timestamp).toLocaleDateString()} — 🕒{" "}
                                {session.hoursUsed || 0.25} {t("hours_unit") || "ساعة"}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

            </>
          )}


        </div>
      </div>
    </>
  );

}

export default TherapistDashboard;
